import type { ReactNode } from 'react'
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import { cx } from '../lib/cx'
import { Empty } from './Page'

export type SortDirection = 'asc' | 'desc'
export interface SortState {
  key: string
  direction: SortDirection
}

export interface Column<T> {
  key: string
  header: ReactNode
  /** Contenu de la cellule ; par défaut `row[key]`. */
  cell?: (row: T) => ReactNode
  sortable?: boolean
  align?: 'start' | 'end'
  /** Colonne numérique : alignée à droite, chiffres tabulaires mono. */
  numeric?: boolean
  width?: string
}

export interface TableProps<T> {
  columns: Column<T>[]
  rows: T[]
  rowKey: (row: T) => string
  /** Tri piloté par le parent (le tri se fait côté API sur les listes paginées). */
  sort?: SortState | null
  onSortChange?: (sort: SortState) => void
  loading?: boolean
  skeletonRows?: number
  empty?: ReactNode
  caption?: string
  onRowClick?: (row: T) => void
  className?: string
  /** Sélection multiple (actions groupées) : case à cocher par ligne et « tout sélectionner ». */
  selection?: {
    selected: readonly string[]
    onChange: (ids: string[]) => void
    /** Libellé accessible de la case d'une ligne. */
    label: (row: T) => string
  }
}

export function Table<T>({
  columns,
  rows,
  rowKey,
  sort,
  onSortChange,
  loading = false,
  skeletonRows = 5,
  empty,
  caption,
  onRowClick,
  className,
  selection,
}: TableProps<T>) {
  const ids = rows.map(rowKey)
  const chosen = new Set(selection?.selected ?? [])
  const allChosen = ids.length > 0 && ids.every((id) => chosen.has(id))
  const someChosen = ids.some((id) => chosen.has(id))
  const toggle = (id: string) =>
    selection?.onChange(chosen.has(id) ? [...chosen].filter((x) => x !== id) : [...chosen, id])
  function toggleSort(key: string) {
    if (!onSortChange) return
    const direction: SortDirection = sort?.key === key && sort.direction === 'asc' ? 'desc' : 'asc'
    onSortChange({ key, direction })
  }

  const showEmpty = !loading && rows.length === 0

  return (
    <div className={cx('tbl-wrap', className)}>
      <table className="tbl" aria-busy={loading || undefined}>
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead>
          <tr>
            {selection && (
              <th scope="col" className="tbl-check">
                <input
                  type="checkbox"
                  aria-label="Tout sélectionner"
                  checked={allChosen}
                  ref={(el) => {
                    if (el) el.indeterminate = someChosen && !allChosen
                  }}
                  onChange={() =>
                    selection.onChange(
                      allChosen
                        ? [...chosen].filter((x) => !ids.includes(x))
                        : [...new Set([...chosen, ...ids])]
                    )
                  }
                />
              </th>
            )}
            {columns.map((column) => {
              const active = sort?.key === column.key
              const ariaSort = active
                ? sort!.direction === 'asc'
                  ? 'ascending'
                  : 'descending'
                : undefined
              const SortIcon = !active
                ? ArrowUpDown
                : sort!.direction === 'asc'
                  ? ArrowUp
                  : ArrowDown
              return (
                <th
                  key={column.key}
                  scope="col"
                  aria-sort={ariaSort}
                  style={{
                    width: column.width,
                    textAlign: column.numeric || column.align === 'end' ? 'end' : undefined,
                  }}
                >
                  {column.sortable && onSortChange ? (
                    <button type="button" onClick={() => toggleSort(column.key)}>
                      {column.header}
                      <SortIcon width={12} height={12} aria-hidden="true" />
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {loading &&
            Array.from({ length: skeletonRows }, (_, index) => (
              <tr key={`skeleton-${index}`} aria-hidden="true">
                {selection && <td className="tbl-check" />}
                {columns.map((column) => (
                  <td key={column.key}>
                    <span
                      className="skeleton"
                      style={{ width: `${50 + ((index * 17 + column.key.length * 7) % 40)}%` }}
                    />
                  </td>
                ))}
              </tr>
            ))}
          {!loading &&
            rows.map((row) => (
              <tr
                key={rowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                style={onRowClick ? { cursor: 'pointer' } : undefined}
                aria-selected={selection ? chosen.has(rowKey(row)) : undefined}
              >
                {selection && (
                  <td className="tbl-check" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label={selection.label(row)}
                      checked={chosen.has(rowKey(row))}
                      onChange={() => toggle(rowKey(row))}
                    />
                  </td>
                )}
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={cx(column.numeric && 'num')}
                    style={column.align === 'end' ? { textAlign: 'end' } : undefined}
                  >
                    {column.cell
                      ? column.cell(row)
                      : String((row as Record<string, unknown>)[column.key] ?? '')}
                  </td>
                ))}
              </tr>
            ))}
          {showEmpty && (
            <tr>
              <td colSpan={columns.length + (selection ? 1 : 0)}>
                {empty ?? <Empty title="Aucun résultat" />}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}
